import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { MainLayout } from '@shared/layouts/MainLayout'
import { AuthLayout } from '@shared/layouts/AuthLayout'
import { PageLoader } from '@/shared/components/ui/page-loader'
import { ROUTES } from '@config/routes'

// ── Lazy imports (each module exports its pages via index.ts) ──────────────
const LoginPage = lazy(() => import('@modules/auth/index').then((m) => ({ default: m.LoginPage })))
const ForgotPasswordPage = lazy(() =>
  import('@modules/auth/index').then((m) => ({ default: m.ForgotPasswordPage })),
)
const ResetPasswordPage = lazy(() =>
  import('@modules/auth/index').then((m) => ({ default: m.ResetPasswordPage })),
)
const DashboardPage = lazy(() =>
  import('@modules/dashboard/index').then((m) => ({ default: m.DashboardPage })),
)
const AdminPage = lazy(() =>
  import('@modules/admin/index').then((m) => ({ default: m.PeoplePage })),
)
const RolePage = lazy(() =>
  import('@modules/admin/index').then((m) => ({ default: m.RoleListPage })),
)
const ChangeLogPage = lazy(() =>
  import('@modules/audit/index').then((m) => ({ default: m.ChangeLogPage })),
)
const CustomersPage = lazy(() =>
  import('@modules/customers/index').then((m) => ({ default: m.CustomersPage })),
)
const CustomerPage = lazy(() =>
  import('@modules/customers/index').then((m) => ({ default: m.CustomerPage })),
)
const CompaniesPage = lazy(() =>
  import('@modules/customers/index').then((m) => ({ default: m.CompaniesPage })),
)
const TemplatesPage = lazy(() =>
  import('@modules/company-roles/index').then((m) => ({ default: m.TemplatesPage })),
)
const MediaPage = lazy(() => import('@modules/media/index').then((m) => ({ default: m.MediaPage })))
const DbServersPage = lazy(() =>
  import('@modules/db-servers/index').then((m) => ({ default: m.DbServersPage })),
)
const PlansPage = lazy(() => import('@modules/plans/index').then((m) => ({ default: m.PlansPage })))
const PlanDetailPage = lazy(() =>
  import('@modules/plans/index').then((m) => ({ default: m.PlanDetailPage })),
)
const RequestLogPage = lazy(() =>
  import('@modules/audit/index').then((m) => ({ default: m.RequestLogPage })),
)
const HomeRedirect = lazy(() =>
  import('@modules/settings/index').then((m) => ({ default: m.HomeRedirect })),
)
const SettingsLayout = lazy(() =>
  import('@modules/settings/index').then((m) => ({ default: m.SettingsLayout })),
)
const SettingsIndex = lazy(() =>
  import('@modules/settings/index').then((m) => ({ default: m.SettingsIndex })),
)
const SettingsSectionRoute = lazy(() =>
  import('@modules/settings/index').then((m) => ({ default: m.SettingsSectionRoute })),
)
const AuthSessionsPage = lazy(() =>
  import('@modules/auth/index').then((m) => ({ default: m.AuthSessionsPage })),
)

// ── Guards (provided by each module) ──────────────────────────────────────
const AuthGuard = lazy(() => import('@modules/auth/index').then((m) => ({ default: m.AuthGuard })))
const AdminGuard = lazy(() =>
  import('@modules/admin/index').then((m) => ({ default: m.AdminGuard })),
)

const withSuspense = (element: React.ReactNode) => (
  <Suspense fallback={<PageLoader />}>{element}</Suspense>
)

export const router = createBrowserRouter([
  // ── Public: Auth ──────────────────────────────────────────────────────
  {
    element: <AuthLayout />,
    children: [
      { path: ROUTES.LOGIN, element: withSuspense(<LoginPage />) },
      { path: ROUTES.FORGOT_PASSWORD, element: withSuspense(<ForgotPasswordPage />) },
      { path: ROUTES.RESET_PASSWORD, element: withSuspense(<ResetPasswordPage />) },
    ],
  },

  // ── Protected: App ────────────────────────────────────────────────────
  {
    element: withSuspense(<AuthGuard />),
    children: [
      {
        element: <MainLayout />,
        children: [
          // Trang chủ: trang mở đầu của tài khoản (Cài đặt › Hiển thị & mặc định) – finiva-admin
          { index: true, element: withSuspense(<HomeRedirect />) },
          { path: ROUTES.DASHBOARD, element: withSuspense(<DashboardPage />) },
          {
            path: ROUTES.SETTINGS,
            element: withSuspense(<SettingsLayout />),
            children: [
              { index: true, element: withSuspense(<SettingsIndex />) },
              { path: ':section', element: withSuspense(<SettingsSectionRoute />) },
            ],
          },
          { path: ROUTES.PROFILE, element: <Navigate to="/settings/account" replace /> },
        ],
      },
    ],
  },

  // ── Protected: Admin ──────────────────────────────────────────────────
  {
    element: withSuspense(<AdminGuard />),
    children: [
      {
        element: <MainLayout />,
        children: [
          { path: ROUTES.ADMIN, element: withSuspense(<AdminPage />) },
          { path: ROUTES.ROLES, element: withSuspense(<RolePage />) },
          { path: ROUTES.AUDIT_LOGS, element: withSuspense(<ChangeLogPage />) },
          { path: ROUTES.REQUEST_LOGS, element: withSuspense(<RequestLogPage />) },
          { path: ROUTES.CUSTOMERS, element: withSuspense(<CustomersPage />) },
          { path: `${ROUTES.CUSTOMERS}/:id`, element: withSuspense(<CustomerPage />) },
          { path: ROUTES.COMPANIES, element: withSuspense(<CompaniesPage />) },
          { path: ROUTES.COMPANY_ROLE_TEMPLATES, element: withSuspense(<TemplatesPage />) },
          { path: ROUTES.DB_SERVERS, element: withSuspense(<DbServersPage />) },
          { path: ROUTES.PLANS, element: withSuspense(<PlansPage />) },
          { path: `${ROUTES.PLANS}/:id`, element: withSuspense(<PlanDetailPage />) },
          { path: ROUTES.MEDIA, element: withSuspense(<MediaPage />) },
          { path: ROUTES.AUTH_SESSIONS, element: withSuspense(<AuthSessionsPage />) },
        ],
      },
    ],
  },

  // ── 404 ───────────────────────────────────────────────────────────────
  { path: '*', element: <Navigate to={ROUTES.DASHBOARD} replace /> },
])
